import React, { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { FaPrint, FaEye } from 'react-icons/fa'
import { MdClose } from 'react-icons/md'
import CustomButton from '../../shared/CustomButton'
import LabReportPrint from './LabReportPrint'
import ReusableModal from '../../shared/ReuseableModal'
import { handlePrintData } from '../../utils/PrintData'
import useFetchData from '../../hooks/UseFetchData'
import SkeletonCardLoading from '../../shared/SkeltonCardLoading'
import RadiologyReportPrint from './RadiologyReportPrint'

const PrintLabReportModal = ({ visible, setVisible, itemId, patient, practitioner, orderNo, data: initialData, radiology, selectedItem }) => {
    const { t } = useTranslation()
    const printRef = useRef(null)
    const { loading: isLoading, executeFetch } = useFetchData()
    const [reportData, setReportData] = useState(initialData || null)

    useEffect(() => {
        if (visible && itemId && !initialData) {
            fetchReportData()
        } else if (initialData) {
            const transformedData = transformReportData(initialData)
            setReportData(transformedData)
        }
    }, [visible, itemId, initialData])

    const transformReportData = (data) => {
        if (data?.orders && Array.isArray(data.orders)) {
            const matchingOrder = data.orders.find(order => order.order_number === orderNo)
            if (matchingOrder) {
                return matchingOrder
            }
            return data.orders[0]
        }

        if (data?.order_items && Array.isArray(data.order_items)) {
            const items = data.order_items.map(item => {
                const history = [];
                if (item.received_info?.at) {
                    history.push({
                        action_performers: {
                            id: item.received_info.by_id || 0,
                            name: item.received_info.by_name || "",
                            role_id: item.received_info.by_role_id || 0,
                            role_name: "",
                            time: item.received_info.at
                        },
                        action_reason: "Order Item Received"
                    });
                }
                if (item.approved_info?.at) {
                    history.push({
                        action_performers: {
                            id: item.approved_info.by_id || 0,
                            name: item.approved_info.by_name || "",
                            role_id: item.approved_info.by_role_id || 0,
                            role_name: "",
                            time: item.approved_info.at
                        },
                        action_reason: "Order Item Result Uploaded"
                    });
                }
                
                return {
                    history: history,
                    item_id: item.id || item._id || item.item_id,
                    item_name: item.non_standard_desc || item.service_desc,
                    result_upload: item.result_upload
                };
            });
            
            return {
                items: items,
                order_number: data.order_no,
                patient_info: data.patient,
                practitioner_info: data.practitioner
            }
        }
        if (data?.history && data?.result_upload) {
            return {
                items: [{
                    history: data.history,
                    item_id: data.item_id,
                    item_name: data.item_name || selectedItem?.non_standard_desc || selectedItem?.service_desc,
                    result_upload: data.result_upload
                }],
                order_number: orderNo,
                patient_info: patient,
                practitioner_info: practitioner
            }
        }
        if (data?.items && Array.isArray(data.items)) {
            return data
        }
        if (Array.isArray(data)) {
            return data
        }

        return data
    }

    const fetchReportData = async () => {
        const url = radiology ? `/v3/radiology/getUploadResults` : `/v3/lab/getUploadResults`;
        const response = await executeFetch('POST', url, {
            item_id: itemId,
            order_no: orderNo
        })
        if (response?.status === 200) {
            const transformedData = transformReportData(response?.data)
            setReportData(transformedData)
        } else if (response?.data) {
            const transformedData = transformReportData(response?.data)
            setReportData(transformedData)
        }
    }

    const handlePrint = async () => {
        if (printRef.current) {
            await handlePrintData(printRef.current.innerHTML, `Lab_Report_${orderNo || ''}_${itemId || ''}`)
        }
    }

    const handleClose = () => {
        setVisible(false)
        setReportData(null)
    }

    return (
        <ReusableModal
            isVisible={visible}
            onClose={handleClose}
            title={t('View Lab Report')}
            HeaderIcon={FaEye}
            className='w-[90%] md:w-[70%] lg:w-[60%]'
            position='top'
            footerButtons={
                <div className="flex items-center gap-2">
                    <CustomButton
                        label={t('Close')}
                        icon={<MdClose />}
                        onClick={handleClose}
                        className='h-[32px]'
                        buttonType="danger"
                    />
                    <CustomButton
                        label={t('Print Report')}
                        icon={<FaPrint />}
                        onClick={handlePrint}
                        className='h-[32px]'
                        disabled={isLoading || !reportData}
                    />
                </div>
            }
        >
            <div className="print-container max-h-[75vh] overflow-y-auto scrollbar">
                {isLoading && !reportData ? (
                    <SkeletonCardLoading count={5} />
                ) : (
                    <div className="print-content" ref={printRef}>
                        <div className="p-4 md:p-6">
                            {radiology ? <RadiologyReportPrint
                                selectedItem={selectedItem}
                                orderData={reportData}
                                selectedItemId={itemId}
                            /> : <LabReportPrint
                                reportData={reportData}
                                patient={patient}
                                practitioner={practitioner}
                            />}
                        </div>
                    </div>
                )}
            </div>
        </ReusableModal>
    )
}

export default PrintLabReportModal
